import { motion } from "framer-motion";
import { MessageSquare, FileText, Handshake } from "lucide-react";

const STEPS = [
    {
        icon: MessageSquare,
        n: "01",
        title: "We Talk",
        desc: "Tell us about your race — the district, the timeline, the office you're running for, where you are today, and what you actually need help with. The first call is free.",
    },
    {
        icon: FileText,
        n: "02",
        title: "We Build a Proposal",
        desc: "You get a written campaign proposal and budget tailored to your race — scope of work, deliverables, timeline, and price. No vague retainers, no surprise add-ons.",
    },
    {
        icon: Handshake,
        n: "03",
        title: "We Negotiate",
        desc: "Every proposal has room. We'll work with you on scope, services, and price until the program fits your campaign — not the other way around.",
    },
];

export default function Pricing() {
    return (
        <section
            id="pricing"
            data-testid="pricing-section"
            className="relative py-28 md:py-36 scratched-black grain overflow-hidden"
        >
            <div className="absolute top-0 inset-x-0 h-px hairline-gold" />

            <div className="mx-auto max-w-7xl px-6 md:px-10 relative">
                {/* Headline */}
                <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 mb-16 lg:mb-20">
                    <div className="lg:col-span-7">
                        <div className="flex items-center gap-3 mb-5">
                            <span className="h-px w-8 bg-gold" />
                            <span className="text-[10px] uppercase tracking-[0.4em] text-gold font-semibold">
                                05 / How Pricing Works
                            </span>
                        </div>
                        <h2
                            data-testid="pricing-headline"
                            className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight"
                        >
                            Built around{" "}
                            <span className="italic text-gold">your race.</span>
                            <br />
                            Not a template.
                        </h2>
                    </div>
                    <p className="lg:col-span-5 text-cream/65 text-base md:text-lg leading-relaxed">
                        Every candidate deserves access to professional campaign
                        services without paying the inflated rates often charged by
                        large consulting firms. Our goal is simple — effective,
                        hands-on support at a price your campaign can actually
                        afford.
                    </p>
                </div>

                {/* 3-step process */}
                <div
                    data-testid="pricing-steps"
                    className="grid grid-cols-1 md:grid-cols-3 gap-px bg-gold/15 border border-gold/15"
                >
                    {STEPS.map((s, i) => {
                        const Icon = s.icon;
                        return (
                            <motion.div
                                key={s.n}
                                initial={{ opacity: 0, y: 16 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{ delay: i * 0.1 }}
                                data-testid={`pricing-step-${i}`}
                                className="bg-[#070707] p-8 md:p-10 relative"
                            >
                                <div className="flex items-start justify-between mb-8">
                                    <div className="w-12 h-12 border border-gold/40 flex items-center justify-center text-gold">
                                        <Icon size={20} strokeWidth={1.6} />
                                    </div>
                                    <span className="font-mono text-[11px] text-gold/70 tracking-widest">
                                        STEP · {s.n}
                                    </span>
                                </div>
                                <h3 className="font-display text-3xl md:text-4xl text-cream mb-4 leading-tight">
                                    {s.title}
                                </h3>
                                <p className="text-cream/65 text-sm md:text-base leading-relaxed">
                                    {s.desc}
                                </p>
                                <div className="mt-8 h-px w-10 bg-gold/40" />
                            </motion.div>
                        );
                    })}
                </div>

                {/* Closing callout */}
                <motion.div
                    initial={{ opacity: 0, y: 14 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    data-testid="pricing-callout"
                    className="mt-12 flex flex-col md:flex-row md:items-center md:justify-between gap-6 border border-gold/30 px-6 md:px-10 py-6 md:py-8"
                >
                    <div className="font-display italic text-2xl md:text-3xl text-cream/95 leading-snug">
                        There is{" "}
                        <span className="text-gold not-italic">always room</span>{" "}
                        for negotiation.
                    </div>
                    <a
                        href="#contact"
                        data-testid="pricing-cta"
                        className="inline-flex items-center justify-center gap-3 bg-gold text-ink px-8 py-4 uppercase tracking-[0.18em] text-xs font-bold hover:bg-gold-light transition-colors whitespace-nowrap"
                    >
                        Start the Conversation
                    </a>
                </motion.div>
            </div>
        </section>
    );
}
